import React, { useState } from "react";
import { Send, Instagram } from "lucide-react";
import { apiClient, SOCIAL } from "@/lib/api";
import { toast } from "sonner";

const DiscordIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M20.317 4.369A19.79 19.79 0 0 0 16.558 3.1a.07.07 0 0 0-.073.035c-.211.375-.445.864-.609 1.249a18.27 18.27 0 0 0-5.487 0 12.51 12.51 0 0 0-.617-1.249.075.075 0 0 0-.074-.035 19.74 19.74 0 0 0-3.76 1.269.07.07 0 0 0-.032.027C2.533 7.96 1.834 11.45 2.18 14.892a.082.082 0 0 0 .031.056 19.9 19.9 0 0 0 5.992 3.029.077.077 0 0 0 .084-.028c.462-.63.873-1.295 1.226-1.994a.076.076 0 0 0-.041-.105 13.11 13.11 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.794 8.18 1.794 12.061 0a.074.074 0 0 1 .078.009c.12.099.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.04.106c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.029.077.077 0 0 0 .031-.055c.5-3.977-.838-7.44-3.548-10.497a.061.061 0 0 0-.031-.027zM8.02 13.331c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.956-2.42 2.157-2.42 1.21 0 2.176 1.097 2.157 2.42 0 1.334-.956 2.42-2.157 2.42zm7.974 0c-1.183 0-2.157-1.086-2.157-2.42 0-1.333.955-2.42 2.157-2.42 1.21 0 2.176 1.097 2.157 2.42 0 1.334-.946 2.42-2.157 2.42z" />
  </svg>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete all required fields");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post("/contact", form);
      toast.success("Request received. Expect a response shortly.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Submission failed. Please try Discord or Instagram.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-black text-white py-24 md:py-32 lg:py-40 border-t border-zinc-900"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-zinc-600" />
              <span className="font-mono text-[10px] md:text-xs tracking-[0.4em] uppercase text-zinc-400">
                Consultation
              </span>
            </div>
            <h2 className="font-display font-light uppercase leading-[1] tracking-tight text-4xl sm:text-5xl md:text-6xl">
              Book a<br />Consultation
            </h2>
            <p className="mt-8 font-sans text-base md:text-lg text-zinc-400 leading-relaxed">
              For private inquiries, reach our operations team directly.
              Consultations are conducted by referral and request.
            </p>

            <div className="mt-12 space-y-px bg-zinc-900">
              <a
                href={SOCIAL.discord}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-discord-link"
                className="flex items-center justify-between gap-4 bg-black p-6 hover:bg-zinc-950 transition-colors duration-300"
              >
                <div className="flex items-center gap-4">
                  <DiscordIcon className="w-6 h-6 text-white" />
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-1">
                      Discord
                    </div>
                    <div className="font-display text-xl text-white">
                      {SOCIAL.discordHandle}
                    </div>
                  </div>
                </div>
                <span className="font-mono text-[10px] tracking-[0.4em] uppercase text-zinc-400">
                  Direct →
                </span>
              </a>
              <a
                href={SOCIAL.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram-link"
                className="flex items-center justify-between gap-4 bg-black p-6 hover:bg-zinc-950 transition-colors duration-300"
              >
                <div className="flex items-center gap-4">
                  <Instagram size={24} strokeWidth={1.5} className="text-white" />
                  <div>
                    <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-1">
                      Instagram
                    </div>
                    <div className="font-display text-xl text-white">
                      @camointelligence
                    </div>
                  </div>
                </div>
                <span className="font-mono text-[10px] tracking-[0.4em] uppercase text-zinc-400">
                  Open →
                </span>
              </a>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              data-testid="contact-form"
              onSubmit={submit}
              className="border border-zinc-900 bg-black p-8 md:p-10 space-y-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">
                    Name *
                  </label>
                  <input
                    data-testid="contact-input-name"
                    value={form.name}
                    onChange={update("name")}
                    required
                    className="w-full bg-transparent border-b border-zinc-800 focus:border-white outline-none py-3 font-sans text-white placeholder:text-zinc-700"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">
                    Email *
                  </label>
                  <input
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    required
                    className="w-full bg-transparent border-b border-zinc-800 focus:border-white outline-none py-3 font-sans text-white placeholder:text-zinc-700"
                    placeholder="you@domain.com"
                  />
                </div>
              </div>

              <div>
                <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">
                  Subject
                </label>
                <input
                  data-testid="contact-input-subject"
                  value={form.subject}
                  onChange={update("subject")}
                  className="w-full bg-transparent border-b border-zinc-800 focus:border-white outline-none py-3 font-sans text-white placeholder:text-zinc-700"
                  placeholder="Membership · Consultation · Operations"
                />
              </div>

              <div>
                <label className="block font-mono text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-3">
                  Message *
                </label>
                <textarea
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={update("message")}
                  required
                  rows={5}
                  className="w-full bg-transparent border-b border-zinc-800 focus:border-white outline-none py-3 font-sans text-white placeholder:text-zinc-700 resize-none"
                  placeholder="Describe your operational requirements..."
                />
              </div>

              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={loading}
                className="group inline-flex items-center justify-between gap-8 bg-white text-black font-mono uppercase text-[11px] tracking-[0.3em] px-8 py-5 hover:bg-zinc-200 transition-colors duration-300 disabled:opacity-50"
              >
                <span>{loading ? "Transmitting..." : "Submit Request"}</span>
                <Send
                  size={14}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
